import { GridEmptyElement } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/GridEmptyElement";
import { ReactNode } from "react";
import { Text, tokens } from "shared/components/Spor";
import styled from "styled-components";

const makeTemplateColumns = (columns: ColumnDefinition[]) =>
  columns
    .map((column) => `[${column.label}] ${column.width ?? "1fr"}`)
    .join(" ");

const HeaderItem = styled.div<{ label: string }>`
  grid-column-start: ${({ label }) => label};
  grid-column-end: span 1;
  grid-row-start: header;
  grid-row-end: header-end;

  font-weight: 700;

  // These padding values may need to be configurable later
  padding-bottom: ${tokens.default.size.spacing[2]};

  &:first-child {
    padding-left: ${tokens.default.size.spacing[2]};
  }
`;

const makeContainerQueries = (
  { breakpoints, containerName = "" }: ResponsiveDefinition,
  allColumns: ColumnDefinition[],
) =>
  breakpoints
    .map(({ maxWidth, includedIndices }) => {
      const [includedColumns, excludedColumns] = allColumns.reduce(
        ([included, excluded], current, index) =>
          includedIndices.includes(index)
            ? [[...included, current], excluded]
            : [included, [...excluded, current]],
        [[], []] as ColumnDefinition[][],
      );

      const excludedColumnsSelector = excludedColumns
        .map(({ label }) => `${HeaderItem}[label="${label}"]`)
        .join(", ");

      return `
        @container ${containerName} (width < ${maxWidth}) {
          grid-template-columns: ${makeTemplateColumns(includedColumns)};

          ${excludedColumnsSelector} {
            display: none;
          }
        }
      `;
    })
    .join("\n\n");

const GridContainer = styled.div<{
  $columns: ColumnDefinition[];
  $responsiveDefinition?: ResponsiveDefinition;
  $rowCount: number;
  $gap?: string;
}>`
  display: grid;
  width: 100%;

  grid-template-columns: ${({ $columns }) => makeTemplateColumns($columns)};
  grid-template-rows:
    [header] auto [header-end content] repeat(
      ${({ $rowCount }) => $rowCount},
      auto
    )
    [content-end];
  gap: ${({ $gap }) => $gap ?? tokens.default.size.spacing[1]};

  ${({ $responsiveDefinition, $columns }) =>
    $responsiveDefinition
      ? makeContainerQueries($responsiveDefinition, $columns)
      : ""}
`;

export type ColumnDefinition = {
  label: string;
  width?: string;
};

type BreakpointDefinition = {
  maxWidth: string;
  includedIndices: number[];
};

type ResponsiveDefinition = {
  containerName?: string;
  breakpoints: BreakpointDefinition[];
};

type DropsTableGridProps<T extends Object> = {
  columns: ColumnDefinition[];
  responsiveDefinition?: ResponsiveDefinition;
  gap?: string;
  items: T[];
  itemRenderer: (item: T, index: number) => ReactNode;
  emptyText?: string;
};

export const DropsTableGrid = <T extends Object>({
  columns,
  responsiveDefinition,
  gap,
  items,
  itemRenderer,
  emptyText,
}: DropsTableGridProps<T>) => {
  if (!columns.length) return null;

  return (
    <GridContainer
      $columns={columns}
      $responsiveDefinition={responsiveDefinition}
      $rowCount={items.length || 1}
      $gap={gap}
    >
      {columns.map(({ label }) => (
        <HeaderItem key={label} label={label}>
          <Text variant="sm">{label}</Text>
        </HeaderItem>
      ))}

      {items.length ? (
        items.map(itemRenderer)
      ) : (
        <GridEmptyElement emptyText={emptyText} />
      )}
    </GridContainer>
  );
};
